import React from "react";
import { Alert, View } from "react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { LucideIcon } from "lucide-react-native";
import { GLASS_CARD_STYLE, semanticColors } from "@louise/moon-ui/theme/tokens";
import { MoonStepper } from "@louise/moon-ui";

export type CounterInputProps = {
  label: string;
  description?: string;
  value: number | null;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
  max?: number;
  icon?: LucideIcon;
  testID?: string;
  decrementConfirmation?: {
    shouldConfirm: () => boolean;
    title: string;
    message: string;
    cancelLabel: string;
    confirmLabel: string;
  };
};

/**
 * Shared numeric answer card for Medical History.
 *
 * Keep the number control compact and neutral: the question card owns the
 * glass surface, while MoonStepper owns the accessible +/- interaction.
 */
export function CounterInput({
  label,
  description,
  value,
  onIncrement,
  onDecrement,
  min = 0,
  max,
  icon: Icon,
  testID,
  decrementConfirmation,
}: CounterInputProps) {
  // Keep an unanswered counter visually empty while giving the stepper a
  // numeric value just below min so the first increment selects min.
  const numericValue = value ?? Math.max(min - 1, 0);

  return (
    <View
      testID={testID}
      style={{
        ...GLASS_CARD_STYLE,
        width: "100%",
        padding: 16,
      }}
    >
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          gap: 16,
        }}
      >
        <View
          style={{
            alignItems: "center",
            flex: 1,
            flexDirection: "row",
            gap: 12,
            minWidth: 0,
          }}
        >
          {Icon && <Icon size={18} color={semanticColors.text.secondary} />}
          <View style={{ flex: 1, gap: 3, minWidth: 0 }}>
            <Text
              className="font-dm-sans-medium"
              style={{
                color: semanticColors.text.primary,
                fontSize: 15,
                fontWeight: "600",
                lineHeight: 20,
              }}
            >
              {label}
            </Text>
            {description && (
              <Text
                className="font-dm-sans"
                style={{
                  color: semanticColors.text.tertiary,
                  fontSize: 12,
                  lineHeight: 17,
                }}
              >
                {description}
              </Text>
            )}
          </View>
        </View>
        <MoonStepper
          value={numericValue}
          min={min}
          max={max}
          displayValue={value ?? "–"}
          accentColor={semanticColors.text.secondary}
          style={{ flexShrink: 0 }}
          onChange={(nextValue) => {
            if (nextValue > numericValue) onIncrement();
            if (nextValue < numericValue) {
              if (decrementConfirmation?.shouldConfirm()) {
                Alert.alert(
                  decrementConfirmation.title,
                  decrementConfirmation.message,
                  [
                    {
                      text: decrementConfirmation.cancelLabel,
                      style: "cancel",
                    },
                    {
                      text: decrementConfirmation.confirmLabel,
                      style: "destructive",
                      onPress: onDecrement,
                    },
                  ],
                );
              } else {
                onDecrement();
              }
            }
          }}
        />
      </View>
    </View>
  );
}
